import clsx from 'clsx'
import { Link } from 'react-router-dom'
import loginBg from './assets/bg.png'
import iconAccount from './assets/icon-account.svg'
import iconPassword from './assets/icon-password.svg'
import logo from './assets/logo.svg'
import { useLogin } from './hooks/useLogin'

const IMAGES = {
  logo,
  iconAccount,
  iconPassword,
  loginBg,
}

export default function Login() {
  const {
    account,
    password,
    showPassword,
    isLoading,
    errorMessage,
    canSubmit,
    setAccount,
    setPassword,
    togglePasswordVisibility,
    handleSignIn,
  } = useLogin()

  return (
    <div
      className={clsx(
        'relative min-h-screen w-full overflow-y-auto overflow-x-hidden flex flex-col items-center',
      )}
      data-testid='login-page'
      data-name='login-page'
      style={{
        backgroundImage: `url(${IMAGES.loginBg})`,
        backgroundPosition: '0 0',
        backgroundSize: 'contain',
        backgroundRepeat: 'space',
      }}
    >
      <div className='relative z-10 flex w-full flex-col items-center px-6 pb-10'>
        {/* Logo */}
        <div className='mt-[80px]'>
          <img
            alt='Logo'
            className='h-[110px] w-[120px]'
            src={IMAGES.logo}
            data-testid='logo'
          />
        </div>

        {/* Title */}
        <div className='mt-[92px] flex justify-center'>
          <div className='relative inline-block px-[20px] py-[10px] backdrop-blur-sm'>
            <div className='absolute inset-0 bg-linear-to-r from-transparent via-white/40 to-transparent' />
            <p
              className='relative whitespace-nowrap text-[20px] leading-[22px] text-[#5c17f2]'
              data-testid='title'
            >
              Sign in to your account
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSignIn}
          noValidate
          className='mt-[18px] flex w-full flex-col gap-[20px]'
        >
          {/* Account Input */}
          <div className='relative flex h-[54px] items-center rounded-[12px] border border-solid border-[#ab93c8] bg-white px-[16px]'>
            <img
              alt=''
              className={clsx(
                'size-[20px] shrink-0 transition-opacity',
                account.length > 0 ? 'opacity-100' : 'opacity-40',
              )}
              src={IMAGES.iconAccount}
            />
            <input
              type='text'
              value={account}
              onChange={(e) => setAccount(e.target.value.replace(/\s/g, ''))}
              disabled={isLoading}
              placeholder='Please enter your account'
              className='ml-[16px] w-full border-none bg-transparent text-[15px] leading-[22px] text-[#5c17f2] placeholder:text-[#9e8ad7] focus:outline-none disabled:opacity-60'
              data-testid='account-input'
            />
          </div>

          {/* Password Input */}
          <div className='flex flex-col gap-[8px]'>
            <div className='relative flex h-[54px] items-center rounded-[12px] border border-solid border-[#ab93c8] bg-white px-[16px]'>
              <img
                alt=''
                className={clsx(
                  'size-[20px] shrink-0 transition-opacity',
                  password.length > 0 ? 'opacity-100' : 'opacity-40',
                )}
                src={IMAGES.iconPassword}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))}
                disabled={isLoading}
                placeholder='Please enter your password'
                className='mx-[16px] w-full border-none bg-transparent text-[15px] leading-[22px] text-[#5c17f2] placeholder:text-[#9e8ad7] focus:outline-none disabled:opacity-60'
                data-testid='password-input'
              />
              {password.length > 0 && (
                <button
                  type='button'
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  className='size-[20px] cursor-pointer shrink-0 disabled:cursor-not-allowed'
                  data-testid='toggle-password'
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    {showPassword ? (
                      <>
                        <path
                          d='M10 4C5.5 4 2 10 2 10s3.5 6 8 6 8-6 8-6-3.5-6-8-6z'
                          stroke='#ab93c8'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <circle
                          cx='10'
                          cy='10'
                          r='2.5'
                          stroke='#ab93c8'
                          strokeWidth='1.5'
                        />
                      </>
                    ) : (
                      <>
                        <path
                          d='M2 2l16 16M6.5 6.5C4.5 7.5 2 10 2 10s3.5 6 8 6c1.5 0 2.8-.4 4-.9m2.5-2.6C17.5 11.5 18 10 18 10s-3.5-6-8-6c-.8 0-1.5.1-2.2.3'
                          stroke='#ab93c8'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </>
                    )}
                  </svg>
                </button>
              )}
            </div>

            {errorMessage && (
              <p
                className='text-[12px] leading-[16px] text-[#FF3B30]'
                data-testid='login-error'
              >
                {errorMessage}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type='submit'
            className={clsx(
              'mt-[10px] h-[54px] w-full rounded-[12px] transition-all disabled:cursor-not-allowed',
              canSubmit
                ? 'bg-linear-to-r from-[#a66fff] to-[#5c17f2] shadow-[0_4px_14px_0_rgba(92,23,242,0.3)] active:scale-[0.98]'
                : 'bg-linear-to-r from-[#d4bcff] to-[#a389f4]',
            )}
            data-testid='sign-in-button'
            disabled={!canSubmit || isLoading}
          >
            <span
              className={clsx(
                'text-[17px] leading-[22px]',
                canSubmit ? 'text-white' : 'text-white/90',
              )}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </span>
          </button>
        </form>

        {/* Terms */}
        <div className='mt-10 w-full text-center' data-testid='terms'>
          <p className='mb-0 text-[12px] leading-[20px] text-[#a09ab1]'>
            By tapping sign in, you accept our
          </p>
          <p className='text-[12px] leading-[20px] text-[#a09ab1]'>
            <Link
              to='/terms-and-conditions'
              className='underline decoration-solid hover:text-[#5c17f2]'
              data-testid='terms-link'
            >
              Terms and Conditions
            </Link>
            <span> and </span>
            <Link
              to='/privacy-policy'
              className='underline decoration-solid hover:text-[#5c17f2]'
              data-testid='privacy-link'
            >
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
